import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import AddAPhoto from '@material-ui/icons/Add';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import type { DialogProps } from '@material-ui/core/Dialog';
import Dialog from '@material-ui/core/Dialog';
import { Button, DialogContent, TextField, Typography } from '@material-ui/core';
import { Alert } from '@mui/material';
import { useRouter } from 'next/router';
import Image from 'next/image';

import { validPhone } from '@jerry-serverless/tools/lib/validators';
import { unformatPhone, formatPhone } from '@jerry-serverless/tools/lib/formatters';

import type { AppData } from '../../../../../../types/app_data';
import { trackEvent } from '../../../../../../utils/analytics';
import { useSaveDocumentUpload } from '../../../../../../hooks/useSaveDocumentUpload';
import type { UploadInfo } from '../../../../../../types/upload';
import { FileCategory, UserDocumentUploadTypeType } from '../../../../../../types/upload';
import { EMAIL_HI } from '../../../../../../utils/constants';

import { FeedbackMethod, sendDoorbellRequest } from './utils';

const MAX_UPLOAD_COUNT = 6;
/**
 * Help modal. Displayed when clicking help button in top nav bar.
 */
export const UnstyledHelpModal: FunctionComponent<Pick<DialogProps, 'open' | 'onClose'> & { appData: AppData }> = (
  props
) => {
  const { appData } = props;
  const [showFAQ, setShowFAQ] = useState(true);
  const [content, setContent] = useState('');
  const [phone, setPhone] = useState<string>(appData.phone || '');
  const [showSubmitTip, setShowSubmitTip] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [images, setImages] = useState<UploadInfo[]>([]);
  const onSaveDocumentUploads = useSaveDocumentUpload(appData.id || '', UserDocumentUploadTypeType.BUG_REPORT);
  const router = useRouter();

  const isValid = useMemo(() => content.length && validPhone(unformatPhone(phone)), [content.length, phone]);

  const needToEnterPhone = useMemo(() => !appData.phone, [appData.phone]);

  const sendBug = useCallback(async () => {
    if (!isValid) {
      return;
    }
    try {
      setReporting(true);
      await onSaveDocumentUploads(images);
      setImages([]);
      await sendDoorbellRequest(FeedbackMethod.Submit, {
        screen: router.pathname?.slice(1),
        phone: phone,
        message: content,
        name: appData.name || '',
        userID: appData.id || '',
        uploads: images.length,
      });
      setShowSubmitTip(true);
      setTimeout(() => {
        setShowSubmitTip(false);
        setContent('');
        setShowFAQ(true);
      }, 2000);
      props.onClose?.({}, 'backdropClick');
      void trackEvent('HelpModalOpen', {
        eventMetadata: {
          path: router.pathname,
          phone: phone,
          message: content,
          from: 'web signup',
          name: appData.name || '',
          userID: appData.id || '',
        },
      });
    } finally {
      setReporting(false);
    }
  }, [appData.id, appData.name, content, images, isValid, onSaveDocumentUploads, phone, props, router.pathname]);

  useEffect(() => {
    if (props.open) {
      void sendDoorbellRequest(FeedbackMethod.Open);
      void trackEvent('HelpModalOpen', {
        eventMetadata: {
          path: router.pathname,
          from: 'web signup',
          name: appData.name || '',
          userID: appData.id || '',
        },
      });
    }
  }, [appData.id, appData.name, props.open, router.pathname]);

  useEffect(() => {
    if (!appData.phone) {
      return;
    }

    setPhone(appData.phone);
  }, [appData.phone]);

  const uploadRef = useRef<HTMLButtonElement | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = useCallback(
    (event) => {
      if (images.length >= MAX_UPLOAD_COUNT) {
        return;
      }
      const files = Array.from(event.target.files);
      const newUploads = files.map((file) => ({
        upload: file as File,
        uri: URL.createObjectURL(file as Blob),
        fileCategory: FileCategory.IMAGE,
      }));
      setImages((prevUploads) => [...prevUploads, ...newUploads].slice(0, 6));
    },
    [images.length]
  );

  const handleRemoveImage = useCallback((index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  }, []);

  return (
    <>
      <Dialog {...props} fullWidth maxWidth="sm">
        <Box className="modal-close">
          <IconButton
            aria-label="close"
            onClick={(evt) => {
              props.onClose?.(evt, 'backdropClick');
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" m={1}>
          <Box className="nav-tab-wrapper">
            <Box display="flex" flexDirection="row" className="nav-tab">
              <div className={showFAQ ? 'show' : ''} onClick={() => setShowFAQ(true)}>
                FAQ
              </div>
              <div
                className={showFAQ ? '' : 'show'}
                onClick={() => {
                  void trackEvent('HelpModalContactUsViewed');
                  setShowFAQ(false);
                }}
              >
                Contact us
              </div>
            </Box>
          </Box>
        </Box>

        {showFAQ && (
          <DialogContent>
            <Typography variant="h4">How much does it cost to use Jerry?</Typography>
            <Typography color="textSecondary">
              Jerry&apos;s insurance shopping platform is free of charge. There are no hidden fees and no surprise
              add-ons at the last minute.
            </Typography>

            <Typography variant="h4">What kind of insurance can Jerry help with?</Typography>
            <Typography color="textSecondary">
              Jerry currently shops for car, home, renters, and umbrella insurance.
            </Typography>

            <Typography variant="h4">Which insurance companies will I get quotes from?</Typography>
            <Typography color="textSecondary">
              Jerry shops with up to 50+ insurance companies. These include Progressive, Nationwide, Travelers, Mapfre,
              National General, Mercury, AAA and others. If you want Jerry to shop at any specific companies, just let
              him know. You can email the member service team at {EMAIL_HI} or call 1-833-445-3779 (M-F 8am-5pm PT).
            </Typography>

            <Typography variant="h4">Does Jerry mark up the quotes from the insurance companies?</Typography>
            <Typography color="textSecondary">
              No. Jerry does not mark up the price. The quotes we send to you are the same as those you&apos;d get if
              you&apos;d shopped yourself.
            </Typography>

            <Typography variant="h4">
              Will Jerry sell my personal information? Will I be bombarded with phone calls and emails?
            </Typography>
            <Typography color="textSecondary">
              Absolutely not. Jerry takes your privacy very seriously. Jerry doesn&apos;t even ask you for your email
              until you&apos;re ready to switch your insurance to a new company. Jerry does not sell or share your
              contact information with any external companies. You will not be getting any phone calls or emails. All
              the communication happens through the mobile app. If you need help, you can request a call from one of our
              in-house licensed agents, but we don&apos;t call to sell you insurance.
            </Typography>

            <Typography variant="h4">
              Do I have to wait until my current insurance policy is up for renewal before I can switch?
            </Typography>
            <Typography color="textSecondary">
              No, you don&apos;t need to wait until you receive a renewal notice from your current provider. You can
              switch insurance policies anytime. By law, insurance companies must return any unused premium you have
              already paid for your policy. A few insurance companies may charge a small cancellation fee if you cancel
              before the end of the policy. Jerry will let you know the cancellation fee before you decide to switch the
              policy.
            </Typography>

            <Typography variant="h4">Is Jerry a licensed insurance agent?</Typography>
            <Typography color="textSecondary">
              Yes, Jerry Insurance Agency (license number 0M34848) is a licensed insurance broker, registered in the
              state of California. Jerry carries a $1m professional liability policy for your peace of mind.
            </Typography>

            <Typography variant="h4">
              How is Jerry better than my local agents or the online price shopping sites?
            </Typography>
            <Typography color="textSecondary">
              1. Jerry provides quotes from up to 50+ insurance companies. Most online price comparison sites (and your
              local agent) will only provide quotes from a few insurance companies (usually the top 5 or 6 companies,
              and a handful of smaller brands) with whom they have contracts. You&apos;re more likely to save money with
              Jerry because we work with top insurance companies.
            </Typography>
            <Typography color="textSecondary">
              2. Jerry&apos;s mobile app makes managing your insurance policy a breeze. You can ask questions, upload
              documents, request changes, and much more. No more phone calls or emails back and forth.
            </Typography>
            <Typography color="textSecondary">
              3. With Jerry, you never have to think about insurance shopping again. Jerry will automatically shop you
              before your renewal date and send you quotes through the mobile app.
            </Typography>

            <Typography variant="h4">Will Jerry change my policy without my approval?</Typography>
            <Typography color="textSecondary">
              Absolutely not. Jerry will not make any changes to either your existing or your new policy without your
              approval. Jerry will shop for you before your policy renewal date and show you all your options in the app
              — it&apos;s up to you to decide if you want to change your policy or not. If you ask Jerry to move forward
              with a quote you like, only then Jerry will do the paperwork to switch your policy.
            </Typography>

            <Typography variant="h4">
              Will having Jerry shop for quotes affect my credit score or current insurance rate?
            </Typography>
            <Typography color="textSecondary">
              No. Frequent shopping for car insurance quotes does not have an impact on your credit score or the cost of
              your insurance. In the state of California, a credit check is not required for getting quotes. In other
              states, insurance companies may run a credit check but it&apos;s a “soft pull,” or fact check, of your
              credit, as opposed to a “hard pull,” which typically occurs when you apply for credit. Soft pulls, which
              are used for comparing car insurance rates, do not affect your credit score or rating, no matter how many
              quotes are obtained.
            </Typography>

            <Typography variant="h4">How can I contact customer service?</Typography>
            <Typography color="textSecondary">
              You can email the member service team at {EMAIL_HI} or call 1-833-445-3779 (M-F 8am-5pm PT).
            </Typography>
          </DialogContent>
        )}
        {!showFAQ && (
          <DialogContent>
            <Typography variant="h6">Help us improve?</Typography>
            <Box>
              <TextField
                multiline
                fullWidth
                minRows={10}
                maxRows={10}
                variant="outlined"
                placeholder="Please let us know what happened"
                className="content-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </Box>
            {appData.isSigned && (
              <>
                <Typography variant="h6">Upload images</Typography>
                <Typography style={{ color: '#6E7E91', marginBottom: 0 }} color="textSecondary">
                  Uploading a screenshot will help us resolve your problem faster.
                </Typography>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  flexWrap="wrap"
                  style={{ marginBottom: 36, marginTop: 12 }}
                >
                  {images.map((image, index) => (
                    <Box className="image-block" key={index}>
                      <Image
                        src={image.uri}
                        style={{ borderRadius: 8 }}
                        width={'108px'}
                        height={'108px'}
                        objectFit="cover"
                        alt={`Preview ${index}`}
                      />
                      <IconButton
                        aria-label="remove"
                        onClick={() => {
                          handleRemoveImage(index);
                        }}
                        className="remove-btn"
                      >
                        <RemoveCircle color="primary" />
                      </IconButton>
                    </Box>
                  ))}
                  {images.length < MAX_UPLOAD_COUNT && (
                    <>
                      <IconButton
                        aria-label="upload"
                        onClick={() => {
                          fileInputRef.current?.click();
                        }}
                        ref={uploadRef}
                        className="upload-btn"
                      >
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                        />
                        <AddAPhoto fontSize="large" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </>
            )}

            {needToEnterPhone && (
              <Box>
                <Typography variant="h6">Please enter your phone number</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="(___) ___ ____"
                  className="phone-input"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value.replace(/[\s(-)]/g, '')))}
                />
              </Box>
            )}

            <Button
              color="primary"
              variant="contained"
              disableElevation
              fullWidth
              onClick={sendBug}
              disabled={!isValid || reporting}
            >
              {reporting ? 'Reporting' : 'Continue'}
            </Button>
          </DialogContent>
        )}
      </Dialog>
      <Dialog open={showSubmitTip}>
        <Box className="submit-tip">
          <Alert severity="success">Thank you, your feedback is highly appreciated!</Alert>
        </Box>
      </Dialog>
    </>
  );
};

export const HelpModal = styled(UnstyledHelpModal)`
  & .MuiDialogContent-root {
    padding: 0px 48px 48px 48px;
  }

  & .MuiPaper-rounded {
    border-radius: 10px;
  }

  & h4 {
    font-size: 1.5rem;
    line-height: 2.25rem;
    font-weight: 400;
    margin-bottom: 1rem;
  }

  & p {
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 400;
    margin-bottom: 1.84rem;
  }

  .image-block {
    margin-right: 1rem;
    position: relative;
    border: 1px solid #d9e0e9;
    border-radius: 0.5rem;
    width: 6.8rem;
    height: 6.8rem;
  }

  .upload-btn {
    border: 1px solid #d9e0e9;
    border-radius: 0.5rem;
    width: 6.8rem;
    height: 6.8rem;
  }

  .remove-btn {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
  }
  .nav-tab-wrapper {
    flex: 1;
    margin: 0 40px;

    .nav-tab {
      margin: 32px 8px 24px;
      height: 2.875rem;
      border: 1px solid #dcdfe1;
      border-radius: 2.5rem;
      font-size: 0.875rem;
      text-align: center;
      padding: 0.25rem;

      div {
        flex: 1;
        height: 2.25rem;
        line-height: 2.25rem;
        border-radius: 1.25rem;
        cursor: pointer;
      }
    }

    .show {
      color: #fff;
      background-color: #ff3975;
    }
  }

  & h6 {
    font-size: 1rem;
  }
  & .content-input {
    margin: 5rem 0 6rem;
    word-break: break-all;

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    .MuiOutlinedInput-multiline {
      padding: 0;
    }

    .MuiOutlinedInput-inputMultiline {
      border: 1px solid #d9e0e9;
      border-radius: 0.5rem;
      padding: 0.625rem;

      &:hover {
        border-color: #0d202f;
      }
    }
  }
  & .phone-input {
    margin: 0.75rem 0 2rem;
  }
  .submit-tip {
    position: absolute;
    top: 10%;
    left: 50%;
    transform: translateX(-50%);
  }

  .modal-close {
    position: absolute;
    top: 0;
    right: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.ipad}px) {
    .MuiDialogContent-root {
      padding: 0px 16px 48px;
    }
    .nav-tab-wrapper {
      margin: 0 10px;
    }
    h4 {
      font-size: 1rem;
      line-height: 1.25rem;
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
    p {
      font-size: 0.875rem;
      line-height: 1.5rem;
    }
  }
`;
